import { computed, effect, inject, Signal, untracked } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { AuthService } from '../services/auth.service';

/** Identidad de sesión para las piezas de jugadores (Astra J3 H3/H4). */
export interface PlayersSessionIdentity {
  /**
   * `id` de la cuenta **resuelta**. `null` sin sesión y también mientras `auth.me()` está
   * en vuelo (identidad pendiente, H4): tras el login de B, `currentUser` sigue siendo A
   * hasta que llega `/identify-user`, pero el token ya es el de B.
   */
  readonly accountId: Signal<number | null>;
  /**
   * Época de sesión: sube con **cada** cambio de identidad, incluidos `null` y el estado
   * pendiente (H3). Un `id` que vuelve a coincidir (A → B → A) es otra época.
   */
  epoch(): number;
  /** Emite con cada cambio de identidad: para cancelar lo que esté en vuelo (`takeUntil`). */
  readonly changes$: Observable<void>;
}

/**
 * Llamar en un contexto de inyección (constructor o inicializador de campo). Una operación
 * captura `accountId()` y `epoch()` al empezar y solo actúa si los dos siguen iguales.
 */
export function playersSessionIdentity(): PlayersSessionIdentity {
  const auth = inject(AuthService);
  const accountId = computed(() => (auth.isLoading() ? null : (auth.currentUser()?.id ?? null)));
  const changes = new Subject<void>();
  let epoch = 0;
  effect(() => {
    accountId();
    untracked(() => {
      epoch++;
      changes.next();
    });
  });
  return { accountId, epoch: () => epoch, changes$: changes.asObservable() };
}
